import { memo, useMemo, useState, useCallback } from 'react';
import { ChartWrapper } from '../shared/ChartWrapper';
import { TRAFFIC_HEX } from '../shared/constants';
import { useOverviewData } from './useOverviewData';

const OUTPUT_TYPES = [
  { name: 'Knowledge Products', key: 'Knowledge product' },
  { name: 'Capacity Sharing', key: 'Capacity sharing for development' },
  { name: 'Innovation Dev', key: 'Innovation development' },
  { name: 'Other Output', key: 'Other output' },
];
const OUTCOME_TYPES = [
  { name: 'Innovation Use', key: 'Innovation use' },
  { name: 'Policy Change', key: 'Policy change' },
  { name: 'Other Outcome', key: 'Other outcome' },
  { name: 'Capacity Change', key: 'Capacity change' },
  { name: 'Innovation Packages', key: 'Innovation Package' },
];
const IMPACT_TYPES = [
  { name: 'Impact Contributions', key: 'Impact contribution' },
];

const IA_CONFIG = [
  { key: 'gender', label: 'Gender', color: '#8B5CF6' },
  { key: 'climate', label: 'Climate', color: '#006837' },
  { key: 'nutrition', label: 'Nutrition', color: '#F37021' },
  { key: 'biodiversity', label: 'Biodiversity', color: '#14B8A6' },
  { key: 'poverty', label: 'Poverty', color: '#007DBC' },
];

const SPHERE_COLORS = {
  output: { bg: '#fffbeb', border: '#d97706', text: '#92400e', label: 'Sphere of Control', subtitle: 'Outputs' },
  outcome: { bg: '#fff1f2', border: '#e11d48', text: '#9f1239', label: 'Sphere of Influence', subtitle: 'Outcomes' },
  impact: { bg: '#faf5ff', border: '#7e22ce', text: '#6b21a8', label: 'Sphere of Interest', subtitle: 'Impact' },
};

function sumTypes(types, byType) {
  return types.reduce((s, t) => s + (byType?.[t.key] || 0), 0);
}

function getTypeCounts(types, byType, byTypeAndFunding) {
  return types
    .map(t => {
      const total = byType?.[t.key] || 0;
      const f = byTypeAndFunding?.[t.key];
      return { ...t, total, w1w2: f?.W1W2 || 0, w3: f?.W3Bilateral || 0 };
    })
    .filter(t => t.total > 0);
}

const TRAFFIC_COLORS = TRAFFIC_HEX;

export const ExecutiveSpheresDiagram = memo(function ExecutiveSpheresDiagram() {
  const { stats, byTypeAndFunding, byImpactArea, areasOfWork, filteredResults } = useOverviewData();
  const [hoveredPill, setHoveredPill] = useState(null);

  const byType = stats?.byType;
  const outputs = useMemo(() => getTypeCounts(OUTPUT_TYPES, byType, byTypeAndFunding), [byType, byTypeAndFunding]);
  const outcomes = useMemo(() => getTypeCounts(OUTCOME_TYPES, byType, byTypeAndFunding), [byType, byTypeAndFunding]);
  const impacts = useMemo(() => getTypeCounts(IMPACT_TYPES, byType, byTypeAndFunding), [byType, byTypeAndFunding]);

  const totalOutputs = sumTypes(OUTPUT_TYPES, byType);
  const totalOutcomes = sumTypes(OUTCOME_TYPES, byType);
  const totalImpact = sumTypes(IMPACT_TYPES, byType);
  const totalResults = totalOutputs + totalOutcomes + totalImpact;

  const iaData = useMemo(() => {
    return IA_CONFIG.map(ia => {
      const counts = byImpactArea?.[ia.key] || { 0: 0, 1: 0, 2: 0 };
      const principal = counts[2] || 0;
      const significant = counts[1] || 0;
      const notTargeted = counts[0] || 0;
      const total = principal + significant + notTargeted;
      const targetedPct = total > 0 ? Math.round(((principal + significant) / total) * 100) : 0;
      return { ...ia, principal, significant, notTargeted, total, targetedPct };
    });
  }, [byImpactArea]);

  const partnerships = stats?.partners?.length || 0;
  const countries = stats?.countries?.length || 0;

  const chartData = useMemo(() => {
    if (!stats) return [];
    return [
      ...outputs.map(t => ({ sphere: 'Outputs', type: t.name, total: t.total, 'W1/W2': t.w1w2, 'W3/Bilateral': t.w3 })),
      ...outcomes.map(t => ({ sphere: 'Outcomes', type: t.name, total: t.total, 'W1/W2': t.w1w2, 'W3/Bilateral': t.w3 })),
      ...impacts.map(t => ({ sphere: 'Impact', type: t.name, total: t.total, 'W1/W2': t.w1w2, 'W3/Bilateral': t.w3 })),
    ];
  }, [stats, outputs, outcomes, impacts]);

  const fmt = n => n.toLocaleString();

  const Pill = useCallback(({ item }) => {
    const isHovered = hoveredPill === item.key;
    return (
      <div
        className="relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/80 border border-current/10 shadow-sm cursor-default transition-all"
        style={{ color: isHovered ? '#111' : '#374151' }}
        onMouseEnter={() => setHoveredPill(item.key)}
        onMouseLeave={() => setHoveredPill(null)}
      >
        <span className="font-semibold">{item.name}</span>
        <span className="text-[10px] opacity-70">
          {fmt(item.total)}
          {isHovered && item.w1w2 > 0 && item.w3 > 0 && (
            <span className="ml-1 text-[9px]">({fmt(item.w1w2)}+{fmt(item.w3)})</span>
          )}
        </span>
      </div>
    );
  }, [hoveredPill]);

  const SphereZone = ({ sphere, types, total, depth }) => {
    const cfg = SPHERE_COLORS[sphere];
    const paddings = { 0: 'p-4', 1: 'p-3 pt-2', 2: 'p-2.5 pt-2' };
    return (
      <div
        className={`rounded-2xl border-2 ${paddings[depth]} transition-all`}
        style={{ borderColor: cfg.border, backgroundColor: cfg.bg }}
      >
        <div className="flex items-center justify-between mb-2">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: cfg.text }}>{cfg.label}</span>
            <span className="text-[10px] ml-2 opacity-60" style={{ color: cfg.text }}>{cfg.subtitle}</span>
          </div>
          <span className="text-sm font-bold tabular-nums" style={{ color: cfg.text }}>{fmt(total)}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {types.map(t => <Pill key={t.key} item={t} />)}
        </div>
      </div>
    );
  };

  if (!stats) return null;

  return (
    <ChartWrapper
      title="Executive Summary — Concentric Spheres"
      info="Nested view of results across CGIAR's three spheres of change (Control, Influence, Interest), combined with Impact Area targeting and Areas of Work progress."
      chartData={chartData}
      sourceResults={filteredResults}
      exportFilename="executive-spheres"
    >
      <div className="space-y-4">
        {/* Concentric spheres */}
        <div
          className="rounded-2xl border-2 p-4"
          style={{ borderColor: SPHERE_COLORS.impact.border, backgroundColor: SPHERE_COLORS.impact.bg }}
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: SPHERE_COLORS.impact.text }}>{SPHERE_COLORS.impact.label}</span>
              <span className="text-[10px] ml-2 opacity-60" style={{ color: SPHERE_COLORS.impact.text }}>{SPHERE_COLORS.impact.subtitle}</span>
            </div>
            <span className="text-sm font-bold tabular-nums" style={{ color: SPHERE_COLORS.impact.text }}>{fmt(totalImpact)}</span>
          </div>

          {impacts.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {impacts.map(t => <Pill key={t.key} item={t} />)}
            </div>
          )}

          {/* Outcomes zone */}
          <div
            className="rounded-2xl border-2 p-3 pt-2"
            style={{ borderColor: SPHERE_COLORS.outcome.border, backgroundColor: SPHERE_COLORS.outcome.bg }}
          >
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: SPHERE_COLORS.outcome.text }}>{SPHERE_COLORS.outcome.label}</span>
                <span className="text-[10px] ml-2 opacity-60" style={{ color: SPHERE_COLORS.outcome.text }}>{SPHERE_COLORS.outcome.subtitle}</span>
              </div>
              <span className="text-sm font-bold tabular-nums" style={{ color: SPHERE_COLORS.outcome.text }}>{fmt(totalOutcomes)}</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {outcomes.map(t => <Pill key={t.key} item={t} />)}
            </div>

            {/* Outputs zone (innermost) */}
            <div
              className="rounded-2xl border-2 p-3 pt-2"
              style={{ borderColor: SPHERE_COLORS.output.border, backgroundColor: SPHERE_COLORS.output.bg }}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: SPHERE_COLORS.output.text }}>{SPHERE_COLORS.output.label}</span>
                  <span className="text-[10px] ml-2 opacity-60" style={{ color: SPHERE_COLORS.output.text }}>{SPHERE_COLORS.output.subtitle}</span>
                </div>
                <span className="text-sm font-bold tabular-nums" style={{ color: SPHERE_COLORS.output.text }}>{fmt(totalOutputs)}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {outputs.map(t => <Pill key={t.key} item={t} />)}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom section: IA gauges + AoW traffic lights */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Impact Area Gauges */}
          <div className="bg-[var(--surface-secondary,#f8fafc)] rounded-xl p-4 border border-[var(--border-primary,#e5e7eb)]">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary,#6b7280)] mb-3">Impact Area Targeting</h4>
            <div className="space-y-2.5">
              {iaData.map(ia => (
                <div key={ia.key}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium" style={{ color: ia.color }}>{ia.label}</span>
                    <span className="text-[10px] text-[var(--text-tertiary,#9ca3af)] tabular-nums">
                      {ia.targetedPct}% targeted ({fmt(ia.principal + ia.significant)}/{fmt(ia.total)})
                    </span>
                  </div>
                  <div className="h-2.5 rounded-full bg-gray-200 overflow-hidden flex">
                    {ia.total > 0 && (
                      <>
                        <div
                          className="h-full transition-all duration-700"
                          style={{ width: `${(ia.principal / ia.total) * 100}%`, backgroundColor: ia.color }}
                        />
                        <div
                          className="h-full transition-all duration-700"
                          style={{ width: `${(ia.significant / ia.total) * 100}%`, backgroundColor: ia.color, opacity: 0.4 }}
                        />
                      </>
                    )}
                  </div>
                  <div className="flex gap-3 mt-0.5 text-[9px] text-[var(--text-tertiary,#9ca3af)]">
                    <span>Principal: {fmt(ia.principal)}</span>
                    <span>Significant: {fmt(ia.significant)}</span>
                    <span>Not targeted: {fmt(ia.notTargeted)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-3 pt-2 border-t border-[var(--border-primary,#e5e7eb)] text-[10px] text-[var(--text-tertiary,#9ca3af)]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-gray-700" /> Principal</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-gray-400" /> Significant</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-gray-200" /> Not targeted</span>
            </div>
          </div>

          {/* AoW Traffic Lights */}
          <div className="bg-[var(--surface-secondary,#f8fafc)] rounded-xl p-4 border border-[var(--border-primary,#e5e7eb)]">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary,#6b7280)] mb-3">Areas of Work Progress</h4>
            {areasOfWork.length > 0 ? (
              <div className="space-y-2">
                {areasOfWork.map(aow => (
                  <div key={aow.id} className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: TRAFFIC_COLORS[aow.ratingKey] || '#9ca3af' }}
                    />
                    <span className="text-xs text-[var(--text-primary,#111827)] flex-1 truncate">{aow.name}</span>
                    <span className="text-[10px] tabular-nums text-[var(--text-tertiary,#9ca3af)]">
                      {aow.achievementPct != null ? `${Math.round(aow.achievementPct)}%` : '—'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[var(--text-tertiary,#9ca3af)] italic">Select a program to see AoW progress</p>
            )}
          </div>
        </div>

        {/* Key metrics footer */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
          <div className="bg-amber-50 rounded-lg p-2.5 border border-amber-200">
            <div className="text-lg font-bold text-amber-800">{fmt(totalOutputs)}</div>
            <div className="text-[10px] text-amber-600">Outputs</div>
          </div>
          <div className="bg-rose-50 rounded-lg p-2.5 border border-rose-200">
            <div className="text-lg font-bold text-rose-800">{fmt(totalOutcomes)}</div>
            <div className="text-[10px] text-rose-600">Outcomes</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-2.5 border border-purple-200">
            <div className="text-lg font-bold text-purple-800">{fmt(totalImpact)}</div>
            <div className="text-[10px] text-purple-600">Impact</div>
          </div>
          <div className="bg-green-50 rounded-lg p-2.5 border border-green-200">
            <div className="text-lg font-bold text-green-800">{fmt(countries)}</div>
            <div className="text-[10px] text-green-600">Countries</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-2.5 border border-blue-200 col-span-2 md:col-span-1">
            <div className="text-lg font-bold text-blue-800">{fmt(partnerships)}</div>
            <div className="text-[10px] text-blue-600">Partners</div>
          </div>
        </div>
      </div>
    </ChartWrapper>
  );
});
